import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import StarRating from "../components/StarRating";
import PreferredAirportCard from "../components/PreferredAirportCard";
import { useAuth } from "../context/AuthContext";

export default function Profile() {
    const { user, refresh } = useAuth();
    const [star, setStar] = useState(null);

    useEffect(() => {
        api.get("/star-power").then((r) => setStar(r.data)).catch(() => {});
    }, []);

    if (!user) return null;
    const chart = user.natal_chart;

    return (
        <Layout>
            <div className="sp-container pt-12 pb-24 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7">
                    <div className="sp-overline">Profile</div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mt-3 leading-[1.05]">{user.name || user.email}</h1>
                    <p className="text-ink-soft mt-3">{user.email}</p>

                    {chart && (
                        <div className="mt-12 grid sm:grid-cols-3 gap-6" data-testid="profile-natal-chart">
                            {[
                                { label: "Sun", value: chart.sun, color: "#C0785E" },
                                { label: "Moon", value: chart.moon, color: "#D4A396" },
                                { label: "Rising", value: chart.rising, color: "#9B9C81" },
                            ].map((c) => (
                                <div key={c.label} className="bg-surface border border-ink/10 rounded-2xl p-6">
                                    <div className="sp-overline" style={{ color: c.color }}>{c.label}</div>
                                    <div className="font-serif text-3xl mt-2">{c.value}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {chart && (
                        <div className="mt-8 bg-surface border border-ink/10 rounded-2xl p-6">
                            <div className="sp-overline">Inner placements (approximate)</div>
                            <div className="mt-3 grid grid-cols-3 gap-6 text-sm">
                                <div><div className="text-ink-muted">Venus</div><div className="font-serif text-lg">{chart.venus}</div></div>
                                <div><div className="text-ink-muted">Mars</div><div className="font-serif text-lg">{chart.mars}</div></div>
                                <div><div className="text-ink-muted">Mercury</div><div className="font-serif text-lg">{chart.mercury}</div></div>
                            </div>
                            <p className="text-xs text-ink-muted mt-4">
                                StarPath uses simplified ephemeris-free calculations. Sun is exact; Moon, Rising and inner planets are good approximations
                                useful for travel mood-matching, not professional natal reading.
                            </p>
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-5">
                    <div className="bg-surface border border-ink/10 rounded-2xl p-8">
                        <div className="sp-overline">Star Power</div>
                        <div className="mt-3">
                            {star ? <StarRating score={star.score} stars={star.stars} size="lg" testid="profile-star-rating" /> : <span className="sp-overline">Loading…</span>}
                        </div>
                        {star && (
                            <div className="mt-8 space-y-4" data-testid="profile-star-breakdown">
                                {Object.entries(star.breakdown).map(([k, v]) => (
                                    <div key={k}>
                                        <div className="flex justify-between text-sm">
                                            <span className="capitalize text-ink-soft">{k}</span>
                                            <span className="font-mono">{v}/25</span>
                                        </div>
                                        <div className="h-1 mt-2 bg-sand-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-planet-sun" style={{ width: `${(v / 25) * 100}%` }} />
                                        </div>
                                    </div>
                                ))}
                                <div className="text-xs text-ink-muted pt-2">Engagement, usage, social, and travel completion each contribute up to 25 points.</div>
                            </div>
                        )}
                    </div>

                    {user.birth && (
                        <div className="mt-6 bg-surface border border-ink/10 rounded-2xl p-8" data-testid="profile-birth">
                            <div className="sp-overline">Birth details</div>
                            <div className="font-serif text-xl mt-2">
                                {user.birth.birth_city}
                                {user.birth.birth_state ? `, ${user.birth.birth_state}` : ""}
                                , {user.birth.birth_country}
                                {user.birth.birth_zip ? ` · ${user.birth.birth_zip}` : ""}
                            </div>
                            <div className="text-sm text-ink-muted mt-1">{user.birth.date_of_birth} · {user.birth.time_of_birth}</div>
                        </div>
                    )}

                    <PreferredAirportCard user={user} onUpdated={() => refresh && refresh()} />
                </aside>
            </div>
        </Layout>
    );
}
